import React from 'react';
import classNames from 'classnames';
import { Switch, Case, Default, When } from 'react-if';

import { Text } from '@nerdwallet/currency/components/Text';

import HoverableBlock from '@components/hoverable-block/hoverable-block';

import styles from './list-item.module.scss';

export const DENSITY = {
  COMFORTABLE: 'comfortable',
  COMPACT: 'compact',
};

type Props = {
  amount?: number | string | React.ReactNode;
  bottomBorder?: boolean;
  className?: string;
  density?: string;
  enabled?: boolean;
  icon?: React.ReactNode;
  inlineColumnContents?: React.ReactNode;
  fixedColumnContents?: React.ReactNode;
  imageSrc?: string;
  imageAlt?: string;
  isIncomeTransaction?: boolean;
  onClick?: () => void;
  rightIcon?: React.ReactNode;
  selected?: boolean;
  subtitle?: React.ReactNode;
  title?: string | React.ReactNode;
  to?: string;
  topBorder?: boolean;
  wideHover?: boolean;
};

const ListItem = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      amount,
      bottomBorder,
      className,
      density = DENSITY.COMPACT,
      enabled = true,
      icon,
      inlineColumnContents,
      fixedColumnContents,
      imageSrc,
      imageAlt,
      isIncomeTransaction,
      onClick,
      rightIcon,
      selected,
      topBorder,
      subtitle,
      title,
      to,
      wideHover = true,
    },
    ref,
  ) => {
    const amountTone = (() => {
      if (enabled) {
        return isIncomeTransaction ? 'positive' : 'default';
      }

      return 'light';
    })();

    return (
      <HoverableBlock
        block
        onClick={onClick}
        to={to}
        wide={wideHover}
        className={className}
        ref={ref}
      >
        <div
          className={classNames(styles.container, {
            [styles['container--top-border']]: topBorder,
            [styles['container--bottom-border']]: bottomBorder,
            [styles['container--disabled']]: !enabled,
            [styles['container--selected']]: selected,
            [styles['container--comfortable']]: density === DENSITY.COMFORTABLE,
          })}
        >
          <When condition={!!fixedColumnContents}>
            <div className={styles.fixedColumn}>{fixedColumnContents}</div>
          </When>

          <When condition={!!inlineColumnContents}>
            <div className={styles.inlineColumn}>{inlineColumnContents}</div>
          </When>

          <When condition={!!icon}>
            <div className={styles.icon}>{icon}</div>
          </When>

          <When condition={!icon && imageSrc}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img alt={imageAlt} className={styles.image} src={imageSrc} />
          </When>

          <div className={styles.description}>
            <div className={styles.title}>
              <Text bold={selected} tone={selected ? 'assist' : 'default'}>
                {title}
              </Text>
            </div>

            <Switch>
              <Case condition={typeof subtitle === 'string'}>
                <Text component="div" size="small" tone="light">
                  {subtitle}
                </Text>
              </Case>
              <Default>{subtitle}</Default>
            </Switch>
          </div>

          <When condition={!!amount}>
            <div className={styles.amount}>
              <Text component="div" tone={amountTone}>
                {amount}
              </Text>
            </div>
          </When>

          <When condition={!!rightIcon}>
            <div className={styles.rightIcon}>{rightIcon}</div>
          </When>
        </div>
      </HoverableBlock>
    );
  },
);

ListItem.displayName = 'ListItem';

export default ListItem;
